import { Input } from "antd";
import { InputProps } from "antd/es/input";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import { useInteractiveCell } from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { ChangeEvent } from "react";
import { FieldValues } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";

type Props<T extends FieldValues> = InputProps & FieldProps<T>;

export const WithInput = <T extends FieldValues = FieldValues>({ formControl, ...props }: Props<T>) => {
    const { onChange, value, className, ...restProps } = props;

    const {
        setControlledValue,
        handleOnSaveSideEffects,
        resolvedValue,
        getLatestValue,
        isFormControlled,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value,
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = event;

        setControlledValue(value);
        onChange?.(event);
    };

    const handleSave = () => {
        handleOnSaveSideEffects();
        props.onSave?.(getLatestValue());
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    }

    return (
        <FieldWrapper
            displayValue={resolvedValue || props.defaultValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
        >
            <WithSaveButton onSave={handleSave}>
                {isFormControlled ? (
                    <FormItem {...(formControl as NonNullable<typeof formControl>)} noStyle>
                        <Input
                            {...restProps}
                            className={classNames(className, "!w-full")}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            onClick={handleClick}
                        />
                    </FormItem>
                ) : (
                    <Input
                        {...restProps}
                        onChange={handleChange}
                        className={classNames(className, "!w-full")}
                        value={resolvedValue as InputProps["value"]}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        onClick={handleClick}
                    />
                )}
            </WithSaveButton>
        </FieldWrapper>
    );
};
